import React, { useState, useEffect, useCallback } from 'react'
import { Input, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import SupplierLogo from 'src/components/shared/SupplierLogo'
import {
  normalizeSupplierTitle,
  rankSuppliersBySearchQuery,
} from 'src/utils/dedupeSuppliers'
import { fetchOMSSuppliers } from 'src/features/SupplierSlice'

const WizardSearchStep = ({
  venueSuppliers = [],
  onSelectSupplier,
  onInviteSupplier,
}) => {
  const dispatch = useDispatch()
  const omsSuppliers = useSelector(state => state.suppliers.omsSuppliers)
  const [searchValue, setSearchValue] = useState('')
  const [filteredSuppliers, setFilteredSuppliers] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (omsSuppliers.length === 0) {
      setIsLoading(true)
      const fetchSuppliers = async () => {
        try {
          await dispatch(fetchOMSSuppliers())
        } catch (error) {
          console.error('Failed to fetch OMS suppliers:', error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchSuppliers()
    }
  }, [dispatch, omsSuppliers.length])

  const handleSearch = useCallback(
    debounce(value => {
      if (!value) {
        setFilteredSuppliers([])
        return
      }
      setFilteredSuppliers(rankSuppliersBySearchQuery(omsSuppliers, value))
    }, 300),
    [omsSuppliers],
  )

  const handleChange = event => {
    const value = event.target.value
    setSearchValue(value)
    handleSearch(value)
  }

  const isSupplierConnected = supplier =>
    venueSuppliers?.some(vs => {
      const supplierMatch =
        vs?.supplierTitle?.toLowerCase() === supplier?.title?.toLowerCase() ||
        vs?.supplier?._id === supplier?._id ||
        vs?.supplier?.title?.toLowerCase() === supplier?.title?.toLowerCase()

      return supplierMatch && vs?.isActive === true
    })

  const handleConnect = supplier => {
    if (isSupplierConnected(supplier)) return
    onSelectSupplier(supplier)
  }

  const renderInviteHelperText = () => {
    const term = searchValue.trim()

    if (!term) {
      return 'Supplier not on OMS yet? Invite them below.'
    }

    if (filteredSuppliers.length === 0 && !isLoading) {
      return `Can't find ${term}? Invite them to OMS.`
    }

    return (
      <>
        None of these? Invite <InviteEmphasis>{term}</InviteEmphasis> instead.
      </>
    )
  }

  return (
    <StepContent>
      <Description>
        Search for suppliers already on OMS and select one to add.
      </Description>
      <SearchHint>Click a supplier or use Add to link them to OMS.</SearchHint>
      <Input
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={handleChange}
        placeholder='Search by supplier name...'
        allowClear
        size='large'
        autoFocus
      />
      {searchValue && filteredSuppliers.length === 0 && !isLoading && (
        <NoResults>No suppliers found</NoResults>
      )}
      {filteredSuppliers.length > 0 && (
        <ResultsList>
          {filteredSuppliers.map(supplier => {
            const isConnected = isSupplierConnected(supplier)
            const resultKey =
              normalizeSupplierTitle(supplier.title) || supplier._id

            return (
              <ResultRow
                key={resultKey}
                $isConnected={isConnected}
                onClick={() => handleConnect(supplier)}
              >
                <SupplierLogo
                  logoUrl={supplier.logoUrl}
                  title={supplier.title}
                  size={40}
                />
                <SupplierName>{supplier.title}</SupplierName>
                {isConnected ? (
                  <AlreadyConnectedBadge>Already added</AlreadyConnectedBadge>
                ) : (
                  <ConnectActionButton
                    type='primary'
                    size='small'
                    onClick={event => {
                      event.stopPropagation()
                      handleConnect(supplier)
                    }}
                  >
                    Add
                  </ConnectActionButton>
                )}
              </ResultRow>
            )
          })}
        </ResultsList>
      )}

      <InviteSection>
        {!isLoading && (
          <InviteHelperText>{renderInviteHelperText()}</InviteHelperText>
        )}
        <InviteButton
          type='default'
          disabled={isLoading}
          onClick={() => onInviteSupplier?.(searchValue.trim())}
        >
          Invite new supplier
        </InviteButton>
      </InviteSection>
    </StepContent>
  )
}

const StepContent = styled.div`
  min-height: 200px;
`

const Description = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 8px;
  text-align: center;
`

const SearchHint = styled.p`
  font-size: 13px;
  color: #595959;
  margin-bottom: 16px;
  text-align: center;
`

const NoResults = styled.p`
  margin-top: 12px;
  font-size: 14px;
  color: #8c8c8c;
  text-align: center;
`

const ResultsList = styled.div`
  margin-top: 8px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  max-height: 280px;
  overflow-y: auto;
`

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: ${({ $isConnected }) => ($isConnected ? 'default' : 'pointer')};
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $isConnected }) =>
      $isConnected ? 'transparent' : '#fafff0'};
  }
`

const SupplierName = styled.span`
  flex: 1;
  font-size: 14px;
  margin-right: 12px;
`

const AlreadyConnectedBadge = styled.span`
  background-color: #f0f0f0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
  flex-shrink: 0;
`

const ConnectActionButton = styled(Button)`
  flex-shrink: 0;
`

const InviteSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  text-align: center;
`

const InviteHelperText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 12px;
`

const InviteEmphasis = styled.span`
  font-weight: 600;
  color: #262626;
`

const InviteButton = styled(Button)`
  min-width: 200px;
  border-color: ${({ theme }) => theme.colours?.lettuceGreen || '#b5d922'};
  color: ${({ theme }) => theme.colours?.bbqBlack || '#1a1a1a'};

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours?.lettuceGreen || '#b5d922'};
    color: ${({ theme }) => theme.colours?.bbqBlack || '#1a1a1a'};
  }
`

export default WizardSearchStep
